/**
 * Transform logic for Now Assist Skill Plugin
 * Contains the toShape function that converts ServiceNow records back to Fluent API format
 */

import {
    type Record as BuildRecord,
    CallExpressionShape,
    Database,
    type Shape,
    type Source,
    type Transform,
    type Diagnostics,
    ts,
} from '@servicenow/sdk-build-core'
import { NowIdShape } from '@servicenow/sdk-build-plugins'
import {
    TABLE_NAMES,
    VAR_SET,
    SECURITY_TABLE_NAMES,
    ACL_CONSTANTS,
    OTHERS_SKILL_FAMILY_SYS_ID,
    EXCLUDED_METRIC_CAPABILITY_IDS,
    STANDARD_OUTPUT_NAMES,
    DEFAULT_CONDITION_SCRIPT,
} from './defaults'
import { generateTools, buildToolsArrowFunctionShape, RawCodeShape } from './tool-processor'
import { generateScriptFromInputs, type InputAttribute as DeployInputAttribute } from './deployment-processor'
import { sanitizeSkillName } from './skill-plugin-helpers'

// Destructure table names for easier usage
const {
    SN_NOWASSIST_SKILL_CONFIG_VAR_SET,
    SYS_ONE_EXTEND_CAPABILITY_DEFINITION,
    SYS_ONE_EXTEND_DEFINITION_CONFIG,
    SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
    SYS_GENERATIVE_AI_CONFIG,
    SYS_GENERATIVE_AI_PROMPT_CONFIG,
    SYS_GENERATIVE_AI_VALIDATOR,
    SYS_GENERATIVE_AI_REQUEST_VALIDATOR,
    SYS_GENERATIVE_AI_RESPONSE_VALIDATOR,
    SYS_VARIABLE_VALUE,
    SYS_ONE_EXTEND_RESOURCE_MAPPING,
    SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
    SYS_ONE_EXTEND_RESOURCE_EDGE,
    SYS_ONE_EXTEND_CAPABILITY,
    SYS_GEN_AI_SKILL_APPLICABILITY,
} = TABLE_NAMES

const { SYS_AGENT_ACCESS_ROLE_CONFIGURATION, SYS_AGENT_ACCESS_ROLE_MAPPING } = SECURITY_TABLE_NAMES

// Type definitions for transform logic
interface NowAssistPanelSettings {
    enabled: true
    roles?: string[]
    conditionMode?: 'simple' | 'advanced'
    conditionScript?: string
}

interface DeploymentSettings {
    uiAction?: Shape
    nowAssistPanel?: NowAssistPanelSettings
    flowAction?: boolean
    skillFamily?: string
}

interface InputAttribute {
    $id: NowIdShape
    name: string
    description: string
    mandatory: boolean
    dataType: string
    truncate?: boolean
    testValues: string
    tableName?: string
    tableSysId?: string
}

interface OutputAttribute {
    $id: NowIdShape
    name: string
    description?: string
    dataType: string
    tableName?: string
}

interface PromptSettingsVersion {
    $id: NowIdShape
    model: string
    temperature: number
    maxTokens?: number
    prompt: string | Shape
    promptState?: 'draft' | 'finalized' | 'published' | 'archived'
    version?: number
    filterCondition?: Record<string, string>
}

interface PromptSettings {
    name: string
    versions: PromptSettingsVersion[]
}

interface ProviderPromptConfig {
    provider: string
    providerAPI?: { type: string; id: string } | string
    prompts: PromptSettings[]
    defaultPrompt?: string
    defaultPromptVersion?: number
}

interface UserAccess {
    $id?: NowIdShape
    type: 'roles' | 'authenticated'
    roles?: Shape[]
}

interface SecurityControls {
    userAccess: UserAccess
    roleRestrictions?: string[]
    roleMap?: Shape[]
}

interface ProviderConfig {
    $id?: NowIdShape
    name: string
    preprocessor?: string | Shape
    postprocessor?: string | Shape
    order?: number
}

// Glue code prefix used by ModuleFunctionShape
export const GLUE_CODE_PREFIX = '// @fluent-module'

/**
 * Check if a script is glue code (module function reference)
 */
export function isGlueCode(script: string): boolean {
    return script.startsWith(GLUE_CODE_PREFIX)
}

/**
 * Regex that matches mustache template references in prompts:
 * - {{ToolName.outputField}} — tool output reference
 * - {{inputfieldname}}       — input field reference (lowercased in XML)
 */
const MUSTACHE_PATTERN = /\{\{([^}]+)\}\}/

/**
 * Check if a prompt string contains any mustache template references.
 */
function hasMustacheReferences(prompt: string): boolean {
    return MUSTACHE_PATTERN.test(prompt)
}

/**
 * Check if a string is a valid JavaScript identifier (can use dot notation)
 */
function isValidJsIdentifier(name: string): boolean {
    // Valid JS identifier: starts with letter/underscore/$, followed by letters/digits/underscores/$
    return /^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(name)
}

/**
 * Format a property access expression, using bracket notation for invalid identifiers
 */
function formatPropertyAccess(base: string, propertyName: string): string {
    if (isValidJsIdentifier(propertyName)) {
        return `${base}.${propertyName}`
    }
    // Use bracket notation for names with spaces or special characters
    return `${base}['${propertyName.replace(/'/g, "\\'")}']`
}

/**
 * Convert a mustache-format prompt string to an arrow function expression.
 *
 * Converts:
 *   {{ToolName.outputField}} → ${p.tool.ToolName.outputField} (only if ToolName exists in toolNames)
 *   {{inputfieldname}}       → ${p.input.originalFieldName} (only if fieldname exists in inputNameMap)
 *   {{unknown.field}}        → {{unknown.field}} (left as-is if not a valid tool or input)
 *
 * Returns a RawCodeShape that produces: (p) => `...${p.tool.X.y}...${p.input.z}...`
 */
function mustacheToArrowFunction(
    prompt: string,
    inputNameMap: Map<string, string>,
    toolNames: Set<string>,
    source: Source
): Shape {
    // Replace each {{...}} reference with the appropriate ${p.*} expression
    const mustacheGlobal = /\{\{([^}]+)\}\}/g
    const templateBody = prompt.replace(mustacheGlobal, (match, ref: string) => {
        if (ref.startsWith('input.')) {
            // Input reference with prefix: {{input.fieldname}} → ${p.input.originalFieldName}
            const fieldName = ref.slice('input.'.length)
            // Only convert if the input exists
            if (inputNameMap.has(fieldName)) {
                const originalName = inputNameMap.get(fieldName)!
                return `\${${formatPropertyAccess('p.input', originalName)}}`
            }
            // Input doesn't exist - leave as-is
            return match
        }
        if (ref.includes('.')) {
            // Potential tool output reference: {{ToolName.outputField}}
            const [toolName, ...outputParts] = ref.split('.')
            // Only convert if the tool exists
            if (toolName && toolNames.has(toolName)) {
                const outputField = outputParts.join('.')
                let expr = 'p.tool'
                expr = formatPropertyAccess(expr, toolName)
                if (outputField) {
                    expr = formatPropertyAccess(expr, outputField)
                }
                return `\${${expr}}`
            }
            // Tool doesn't exist - leave as-is
            return match
        }
        // Input reference without prefix: {{inputfieldname}} → ${p.input.originalFieldName}
        // Only convert if the input exists
        if (inputNameMap.has(ref)) {
            const originalName = inputNameMap.get(ref)!
            return `\${${formatPropertyAccess('p.input', originalName)}}`
        }
        // Input doesn't exist - leave as-is
        return match
    })

    // Escape any literal backticks in the template body so the output template literal is valid.
    // We must not escape our ${p.*} expressions — only standalone backtick characters.
    // Split on ${...} to protect expressions, escape backticks in text parts, then rejoin.
    const parts = templateBody.split(/(\$\{[^}]+\})/)
    const escapedParts = parts.map((part) => {
        if (part.startsWith('${') && part.endsWith('}')) {
            return part // Expression — leave as-is
        }
        return part.replace(/`/g, '\\`') // Text — escape backticks
    })
    const escapedBody = escapedParts.join('')
    const arrowCode = '(p) => `' + escapedBody + '`'
    return new RawCodeShape({ source, code: arrowCode })
}

/**
 * Post-process providers array: convert any prompt strings containing mustache
 * template references into arrow function shapes for proper Fluent code generation.
 *
 * Mutates the providers array in place, replacing string prompts with RawCodeShape
 * where mustache patterns are detected.
 *
 * Only converts references that match valid inputs or tools. Unknown references
 * are left as-is (e.g., {{incident.description}} stays unchanged if 'incident'
 * is not a defined tool or input).
 */
function convertMustachePromptsToArrowFunctions(
    providers: ProviderPromptConfig[],
    inputNameMap: Map<string, string>,
    toolNames: Set<string>,
    source: Source
): void {
    for (const provider of providers) {
        for (const promptSettings of provider.prompts) {
            for (const version of promptSettings.versions) {
                if (typeof version.prompt === 'string' && hasMustacheReferences(version.prompt)) {
                    version.prompt = mustacheToArrowFunction(version.prompt, inputNameMap, toolNames, source)
                }
            }
        }
    }
}

interface SkillSettings {
    providers?: ProviderConfig[]
}

/**
 * Transform ServiceNow records back to Fluent API format
 * This is the toShape function for the plugin
 */
export async function transformToShape(
    record: BuildRecord,
    descendantsDB: Database,
    _transform: Transform,
    diagnostics: Diagnostics,
    database?: Database
) {
    const excludedRecords: BuildRecord[] = []
    const supportedRecords: BuildRecord[] = []
    descendantsDB.query().forEach((descendant) => {
        const table = descendant.getTable()
        const isAutoTunedConfig =
            table === SYS_GENERATIVE_AI_CONFIG && descendant.get('state')?.getValue() === 'auto_tuned'
        const isExcludedMetricMapping =
            table === SYS_ONE_EXTEND_RESOURCE_MAPPING &&
            EXCLUDED_METRIC_CAPABILITY_IDS.has(descendant.get('resource_capability').toString().getValue())

        if (isAutoTunedConfig || isExcludedMetricMapping) {
            excludedRecords.push(descendant)
        } else {
            supportedRecords.push(descendant)
        }
    })
    const descendants = new Database(supportedRecords)
    // Record is now the skill config (root of hierarchy)
    const skillConfig = record
    const capabilities = descendants.query('sys_one_extend_capability')
    // Find the skill capability (type='skill') — tool capabilities have type='datasource'
    // When tools are present, descendants include both the skill capability and tool capabilities,
    // so we must not just take capabilities[0].
    const capability = capabilities.find((cap) => cap.get('type')?.getValue() === 'skill') ?? capabilities[0]
    if (!capability) {
        // Not a Now Assist skill - skip it (no capability found)
        return {
            success: false as const,
            error: 'Not a Now Assist skill - no capability found',
        }
    }

    // Extract AI config and definition config
    const allAiConfigs = descendants.query(SYS_GENERATIVE_AI_CONFIG)
    const definitionConfigs = descendants.query(SYS_ONE_EXTEND_DEFINITION_CONFIG)
    const capabilityDefinitions = descendants.query(SYS_ONE_EXTEND_CAPABILITY_DEFINITION)

    const capabilityId = capability.getId().getValue()

    // Build a set of capability definition IDs that belong to this skill's capability
    // sys_generative_ai_config.definition points to sys_one_extend_capability_definition
    // sys_one_extend_capability_definition.capability points to sys_one_extend_capability
    const capabilityDefinitionIds = new Set<string>()
    for (const capDef of capabilityDefinitions) {
        const capDefCapability = capDef.get('capability')?.getValue()
        if (capDefCapability === capabilityId) {
            const capDefId = capDef.getId().getValue()
            if (capDefId) {
                capabilityDefinitionIds.add(capDefId)
            }
        }
    }

    // Filter AI configs to only include those whose 'definition' field matches
    // one of the capability definitions belonging to this skill
    const aiConfigs = allAiConfigs.filter((config) => {
        const configDefinition = config.get('definition')?.getValue()
        return configDefinition && typeof configDefinition === 'string' && capabilityDefinitionIds.has(configDefinition)
    })

    // Find the default definition config (only one can be default)
    const defaultDefinitionConfig = definitionConfigs.find((config) => {
        const defaultValue = config.get('default')?.getValue()
        return defaultValue === true || defaultValue === 'true'
    })

    // The default provider is determined by sys_one_extend_definition_config.default=true
    const defaultDefinitionId = defaultDefinitionConfig?.get('definition')?.getValue()

    // Extract prompt config records to find default prompts per provider
    const promptConfigs = descendants.query(SYS_GENERATIVE_AI_PROMPT_CONFIG)

    // Extract validator records — filter junction records to only include those
    const validators = descendants.query(SYS_GENERATIVE_AI_VALIDATOR)
    const requestValidators = descendants.query(SYS_GENERATIVE_AI_REQUEST_VALIDATOR).filter((rv) => {
        const docId = rv.get('document_id')?.getValue()
        return docId && typeof docId === 'string' && capabilityDefinitionIds.has(docId)
    })
    const responseValidators = descendants.query(SYS_GENERATIVE_AI_RESPONSE_VALIDATOR).filter((rv) => {
        const defId = rv.get('definition')?.getValue()
        return defId && typeof defId === 'string' && capabilityDefinitionIds.has(defId)
    })
    // additionalFields are not used in generated code, so these queries are commented out
    const definitionAttributes = descendants.query(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE)
    const varSetRecords = descendants.query(SN_NOWASSIST_SKILL_CONFIG_VAR_SET)
    const variableValues = descendants.query(SYS_VARIABLE_VALUE)

    // Query security records - these are descendants of the skill config, not the capability directly
    let allAclRecords = descendants.query('sys_security_acl')
    const allAclRoleRecords = descendants.query('sys_security_acl_role')
    const roleConfigRecords = descendants.query(SYS_AGENT_ACCESS_ROLE_CONFIGURATION)

    // Get internal_name for ACL matching
    let internalName = skillConfig.get('internal_name')?.getValue()
    const skillSysId = skillConfig.getId()?.getValue()

    // During IDE incremental sync, internal_name may not be present on the record
    // (it's a computed field set by toFile/skill-processor, and IDE sync only pulls changed records).
    // Compute it from skill name + sys_id as a fallback — same logic as toFile and skill-processor.
    if (!internalName && skillSysId) {
        const skillName = skillConfig.get('name')?.toString().getValue()
        if (skillName) {
            internalName = `${sanitizeSkillName(skillName)}.${skillSysId}`
        }
    }

    // Fallback to database lookup if descendants query returns empty
    // This handles incremental updates where the via function may not match
    // (similar to how ai-agentic-workflow-plugin uses database.get() directly)
    if (allAclRecords.length === 0 && database && typeof internalName === 'string') {
        const matchingAcl = database.get('sys_security_acl', { name: internalName })
        if (matchingAcl) {
            allAclRecords = [matchingAcl]
        } else if (skillSysId) {
            // Try to find ACL by sys_id suffix pattern (handles skill name changes)
            const allAcls = database.query('sys_security_acl')
            const suffixMatch = allAcls.find((acl: BuildRecord) => {
                const aclName = acl.get('name')?.getValue()
                return typeof aclName === 'string' && aclName.endsWith(`.${skillSysId}`)
            })
            if (suffixMatch) {
                allAclRecords = [suffixMatch]
            }
        }
    }

    // Filter ACL records by internal_name to ensure we get the correct one
    // This handles cases where the custom via function doesn't match correctly
    let aclRecords = internalName
        ? allAclRecords.filter((acl: BuildRecord) => acl.get('name')?.getValue() === internalName)
        : allAclRecords

    // Fallback: if no match found by internal_name, try sys_id suffix match
    // This handles the case where skill name was changed from UI and internal name got updated
    if (aclRecords.length === 0 && skillSysId) {
        aclRecords = allAclRecords.filter((acl: BuildRecord) => {
            const aclName = acl.get('name')?.getValue()
            return typeof aclName === 'string' && aclName.endsWith(`.${skillSysId}`)
        })
    }

    // Filter ACL role records to only those belonging to our skill's ACL
    let aclRoleRecords: BuildRecord[] = []
    if (aclRecords.length > 0) {
        const firstAclRecord = aclRecords[0]
        if (firstAclRecord) {
            const aclSysId = firstAclRecord.getId().getValue()
            aclRoleRecords = allAclRoleRecords.filter((aclRole: BuildRecord) => {
                const aclRef = aclRole.get('sys_security_acl')?.toString().getValue()
                return aclRef === aclSysId
            })
        }
    }

    // Build deployConfig from sys_variable_value records
    let deployConfigData: DeploymentSettings | null = null
    if (variableValues.length > 0) {
        // Find the var set record that has deployment-related variables
        const deploymentVarSet = varSetRecords.find((varSet) => {
            const varSetId = varSet.getId().getValue()
            // Check if this var set has any deployment-related variables
            return variableValues.some(
                (vv) =>
                    vv.get('document_key')?.getValue() === varSetId &&
                    [
                        VAR_SET.ENABLE_UI_ACTION,
                        VAR_SET.UI_ACTION_ID,
                        VAR_SET.ENABLE_UI_BUILDER,
                        VAR_SET.ENABLE_NAP,
                    ].includes(vv.get('variable')?.getValue() as string)
            )
        })

        // Find the View Trigger var set by config_type or by having ENABLE_WORKFLOW_TRIGGER variable
        const flowActionVarSet = varSetRecords.find((varSet) => {
            // Check by config_type first (more reliable)
            const configType = varSet.get('config_type')?.getValue()
            if (configType === VAR_SET.CONFIG_TYPE_VIEW_TRIGGER_STEP) {
                return true
            }
            // Fallback: check if this var set has ENABLE_WORKFLOW_TRIGGER variable
            const varSetId = varSet.getId().getValue()
            return variableValues.some(
                (vv) =>
                    vv.get('document_key')?.getValue() === varSetId &&
                    vv.get('variable')?.getValue() === VAR_SET.ENABLE_WORKFLOW_TRIGGER
            )
        })

        if (deploymentVarSet || flowActionVarSet) {
            // Build deployment settings as object (new format)
            const deploymentSettings: DeploymentSettings = {}

            // Extract values from deployment var set
            if (deploymentVarSet) {
                const varSetId = deploymentVarSet.getId().getValue()
                const relatedVariables = variableValues.filter((vv) => vv.get('document_key')?.getValue() === varSetId)

                let uiActionId: string | undefined
                let napEnabled = false

                for (const varValue of relatedVariables) {
                    const variable = varValue.get('variable')?.getValue() as string
                    const value = varValue.get('value')?.getValue()

                    if (variable === VAR_SET.UI_ACTION_ID) {
                        uiActionId = value as string
                    } else if (variable === VAR_SET.ENABLE_NAP) {
                        napEnabled = value === '1' || value === 'true'
                    }
                }

                // Add UIAction — use descendants (sys_ui_action is a descendant via sys_variable_value)
                // Use .def() transform helper so the commit system automatically omits default values
                if (uiActionId) {
                    const uiActionRecords = descendants.query('sys_ui_action')
                    const uiActionRecord = uiActionRecords.find((rec) => String(rec.getId().getValue()) === uiActionId)
                    if (uiActionRecord) {
                        // Compute defaults matching deployment-processor.ts build defaults
                        const skillName = String(skillConfig.get('name')?.getValue() || '')
                        const defaultCondition = `new sn_nowassist_admin.NowAssistSkillConfig().isSkillConfigurationActive('${skillSysId}')`
                        const inputAttrs = definitionAttributes.filter(
                            (attr) =>
                                attr.get('type')?.getValue() === 'input' &&
                                attr.get('capability')?.getValue() === capabilityId
                        )
                        const deployInputs: DeployInputAttribute[] = inputAttrs.map((attr) => {
                            const name = String(attr.get('name')?.getValue() || '')
                            const dataType = String(attr.get('data_type')?.getValue() || 'string')
                            let tblName = ''
                            const schemaVal = attr.get('schema')?.getValue()
                            if (schemaVal) {
                                try {
                                    const schema = typeof schemaVal === 'string' ? JSON.parse(schemaVal) : schemaVal
                                    tblName = schema.tableName || ''
                                } catch {}
                            }
                            return { name, dataType, mandatory: false, description: '', tableName: tblName }
                        })
                        const defaultScript = generateScriptFromInputs(
                            String(skillSysId),
                            String(capabilityId),
                            deployInputs
                        )

                        // Build shape with .def() — commit system omits properties that match defaults
                        deploymentSettings.uiAction = uiActionRecord.transform(({ $ }) => ({
                            $id: $.val(NowIdShape.from(uiActionRecord)),
                            table: $.from('table'),
                            name: $.from('name').def(skillName),
                            condition: $.from('condition').def(defaultCondition),
                            script: $.from('script').def(defaultScript),
                            active: $.from('active').toBoolean().def(false),
                        }))
                    }
                }

                // Add NAP if enabled - extract roles from sys_gen_ai_skill_applicability record
                if (napEnabled) {
                    // Query NAP applicability records to get roles from instance
                    const napApplicabilityRecords = descendants.query(SYS_GEN_AI_SKILL_APPLICABILITY)
                    const napRoles: string[] = []

                    let napConditionMode: 'simple' | 'advanced' | undefined
                    let napConditionScript: string | undefined
                    if (napApplicabilityRecords.length > 0) {
                        const napRecord = napApplicabilityRecords[0]
                        const rolesValue = napRecord?.get('roles')?.getValue() as string
                        if (rolesValue) {
                            // roles is stored as comma-separated string - extract exactly what's in instance
                            const parsedRoles = rolesValue
                                .split(',')
                                .map((r) => r.trim())
                                .filter((r) => r.length > 0)
                            napRoles.push(...parsedRoles)
                        }
                        // Read condition_mode from instance to preserve on round-trip
                        const conditionModeValue = napRecord?.get('condition_mode')?.getValue() as string
                        if (conditionModeValue === 'simple' || conditionModeValue === 'advanced') {
                            napConditionMode = conditionModeValue
                        }
                        // Read condition_script when condition_mode is 'advanced'
                        // Skip if it matches the default template — no need to surface boilerplate to users
                        if (napConditionMode === 'advanced') {
                            const scriptValue = napRecord?.get('condition_script')?.getValue() as string
                            if (scriptValue && scriptValue !== DEFAULT_CONDITION_SCRIPT) {
                                napConditionScript = scriptValue
                            }
                        }
                    }

                    deploymentSettings.nowAssistPanel = {
                        enabled: true,
                        // Include roles only when present. Empty roles (valid in advanced
                        // mode) are omitted so the regenerated source matches the record.
                        ...(napRoles.length > 0 ? { roles: napRoles } : {}),
                        // Only include conditionMode if it differs from the default 'simple'
                        ...(napConditionMode && napConditionMode !== 'simple'
                            ? { conditionMode: napConditionMode }
                            : {}),
                        // Include conditionScript when conditionMode is 'advanced'
                        ...(napConditionScript ? { conditionScript: napConditionScript } : {}),
                    }
                }
            }

            // Extract EnableFlowAction from flow action var set
            // The existence of a View Trigger var set indicates flowAction is enabled
            // The value '0' or '1' in sys_variable_value represents whether the workflow trigger
            // is activated, not whether the flowAction deployment option is enabled
            if (flowActionVarSet) {
                deploymentSettings.flowAction = true
            }

            // Set deployConfigData to the object if any options are enabled
            if (Object.keys(deploymentSettings).length > 0) {
                deployConfigData = deploymentSettings
            }
        }
    }

    // Include skillFamily in deploymentSettings only when it differs from the "Others" default,
    // to keep generated code clean when the skill uses the default family.
    const skillFamilyValue = skillConfig.get('skill_family')?.getValue() as string | undefined
    if (skillFamilyValue && skillFamilyValue !== OTHERS_SKILL_FAMILY_SYS_ID) {
        if (!deployConfigData) {
            deployConfigData = {}
        }
        deployConfigData.skillFamily = skillFamilyValue
    }

    // Build promptConfiguration structure
    // Extract inputs and outputs from definition attributes
    // IMPORTANT: Only include attributes that belong to the main skill capability, not tool capabilities
    const inputAttrs = definitionAttributes.filter(
        (attr) => attr.get('type')?.getValue() === 'input' && attr.get('capability')?.getValue() === capabilityId
    )
    const outputAttrs = definitionAttributes.filter(
        (attr) => attr.get('type')?.getValue() === 'output' && attr.get('capability')?.getValue() === capabilityId
    )

    // Build inputs array (excluding standard outputs like error, errorcode, status, provider)
    const inputs: InputAttribute[] = []
    for (const inputAttr of inputAttrs) {
        const attrName = inputAttr.get('name')?.getValue()?.toString()
        if (attrName) {
            // Convert mandatory field properly - it might be stored as string in DB
            const mandatoryValue = inputAttr.get('mandatory')?.getValue()
            const mandatory = mandatoryValue === true || mandatoryValue === 'true' || mandatoryValue === '1'

            const dataTypeValue = inputAttr.get('data_type')?.getValue()
            const dataType = typeof dataTypeValue === 'string' ? dataTypeValue : 'string'

            // Convert truncate field properly - it might be stored as string in DB
            const truncateValue = inputAttr.get('truncate')?.getValue()
            const truncate = truncateValue === true || truncateValue === 'true' || truncateValue === '1'

            // Data types that do NOT support truncate
            const nonTruncatableTypes = ['simple_array', 'json_object', 'json_array']
            const supportsTruncate = !nonTruncatableTypes.includes(dataType as string)

            // Parse schema once to extract table info
            const schemaValue = inputAttr.get('schema')?.getValue()
            let tableName = '',
                tableSysId = ''
            if (schemaValue) {
                try {
                    const schema = typeof schemaValue === 'string' ? JSON.parse(schemaValue) : schemaValue
                    tableName = schema.tableName || ''
                    tableSysId = schema.sysId || ''
                } catch {}
            }

            const labelValue = inputAttr.get('label')?.getValue()
            const descriptionValue = inputAttr.get('description')?.getValue()
            const defaultValue = inputAttr.get('default_value')?.getValue()

            inputs.push({
                $id: NowIdShape.from(inputAttr),
                name: typeof labelValue === 'string' ? labelValue : attrName,
                description: typeof descriptionValue === 'string' ? descriptionValue : '',
                mandatory: mandatory,
                dataType: dataType,
                ...(supportsTruncate ? { truncate: truncate } : {}),
                testValues: typeof defaultValue === 'string' ? defaultValue : '',
                ...(tableName || tableSysId ? { tableName: tableName, tableSysId: tableSysId } : {}),
            })
        }
    }

    // Build outputs array — standard outputs first, then custom outputs
    const outputs: OutputAttribute[] = []
    // First pass: add the 5 standard outputs in the correct order
    for (const stdName of STANDARD_OUTPUT_NAMES) {
        const stdAttr = outputAttrs.find((attr) => attr.get('name')?.getValue()?.toString() === stdName)
        if (stdAttr) {
            const dataTypeValue = stdAttr.get('data_type')?.getValue()
            outputs.push({
                $id: NowIdShape.from(stdAttr),
                name: stdName,
                dataType: typeof dataTypeValue === 'string' ? dataTypeValue : 'string',
            })
        }
    }
    // Second pass: add custom outputs (excluding standard ones)
    for (const outputAttr of outputAttrs) {
        const attrName = outputAttr.get('name')?.getValue()?.toString()
        if (attrName && !(STANDARD_OUTPUT_NAMES as readonly string[]).includes(attrName)) {
            const labelValue = outputAttr.get('label')?.getValue()
            const descriptionValue = outputAttr.get('description')?.getValue()
            const dataTypeValue = outputAttr.get('data_type')?.getValue()

            // Parse schema to extract table info for glide_record type
            const schemaValue = outputAttr.get('schema')?.getValue()
            let tableName = ''
            if (schemaValue) {
                try {
                    const schema = typeof schemaValue === 'string' ? JSON.parse(schemaValue) : schemaValue
                    tableName = schema.tableName || ''
                } catch {}
            }

            outputs.push({
                $id: NowIdShape.from(outputAttr),
                name: typeof labelValue === 'string' ? labelValue : attrName,
                description: typeof descriptionValue === 'string' ? descriptionValue : '',
                dataType: typeof dataTypeValue === 'string' ? dataTypeValue : 'string',
                ...(tableName ? { tableName: tableName } : {}),
            })
        }
    }

    // Pre-build maps of definitionId -> provider info for efficient lookup
    const definitionIdToProvider = new Map<string, string>()
    const definitionIdToProviderAPI = new Map<string, { type: string; id: string }>()
    for (const capabilityDefinition of capabilityDefinitions) {
        const definitionId = capabilityDefinition.getId().getValue()

        // Extract provider name from capability_definition.name field
        // Format: "{skillName} ({providerName})" - this is what's used for coalesce
        // This is more reliable than filter_properties.provider which can be inconsistent
        const definitionName = capabilityDefinition.get('name')?.getValue()
        if (definitionName && typeof definitionName === 'string') {
            // Extract provider name from parentheses: "skillName (Provider Name)" -> "Provider Name"
            const match = definitionName.match(/\(([^)]+)\)$/)
            if (match && match[1]) {
                definitionIdToProvider.set(definitionId, match[1])
            }
        }

        // Fallback to filter_properties.provider if name parsing fails
        if (!definitionIdToProvider.has(definitionId)) {
            const filterPropertiesValue = capabilityDefinition.get('filter_properties')?.getValue()
            if (filterPropertiesValue) {
                try {
                    const filterProperties =
                        typeof filterPropertiesValue === 'string'
                            ? JSON.parse(filterPropertiesValue)
                            : filterPropertiesValue
                    if (filterProperties.provider) {
                        definitionIdToProvider.set(definitionId, filterProperties.provider)
                    }
                } catch {}
            }
        }

        // Extract providerAPI (flow implementation) from api and api_type fields
        const apiValue = capabilityDefinition.get('api')?.getValue()
        const apiTypeValue = capabilityDefinition.get('api_type')?.getValue()
        if (apiValue && typeof apiValue === 'string' && apiTypeValue && typeof apiTypeValue === 'string') {
            definitionIdToProviderAPI.set(definitionId, { type: apiTypeValue, id: apiValue })
        }
    }

    // Build a map of ai_config_id -> is_default from prompt configs
    const aiConfigIdToIsDefault = new Map<string, boolean>()
    // Build a map of ai_config_id -> filter_properties from prompt configs
    const aiConfigIdToFilterProperties = new Map<string, string>()
    for (const promptConfig of promptConfigs) {
        const aiConfigId = promptConfig.get('ai_config')?.getValue()
        const isDefault = promptConfig.get('is_default')?.getValue()
        const filterProperties = promptConfig.get('filter_properties')?.getValue()
        if (aiConfigId && typeof aiConfigId === 'string') {
            aiConfigIdToIsDefault.set(aiConfigId, isDefault === true || isDefault === 'true' || isDefault === '1')
            if (filterProperties && typeof filterProperties === 'string') {
                aiConfigIdToFilterProperties.set(aiConfigId, filterProperties)
            }
        }
    }

    // Build providers structure - group prompts by provider and name, then by version
    // Note: Tool names will be collected later after tools are generated
    const providerPromptsMap = new Map<
        string,
        {
            providerAPI: { type: string; id: string } | undefined
            promptsByName: Map<string, { versions: PromptSettingsVersion[] }>
            defaultPromptName?: string
            defaultPromptVersion?: number
        }
    >()

    for (const config of aiConfigs) {
        const configId = config.getId().getValue()
        const definitionId = config.get('definition')?.getValue()
        const provider =
            definitionId && typeof definitionId === 'string'
                ? (definitionIdToProvider.get(definitionId) ?? 'Now LLM Service')
                : 'Now LLM Service'
        const providerAPI =
            definitionId && typeof definitionId === 'string' ? definitionIdToProviderAPI.get(definitionId) : undefined

        const nameValue = config.get('name')?.getValue()
        const modelValue = config.get('model')?.getValue()
        const promptValue = config.get('prompt')?.getValue()
        const stateValue = config.get('state')?.getValue()
        const versionValue = config.get('version')?.getValue()
        const promptName = typeof nameValue === 'string' ? nameValue : 'AI-generated prompt'
        // Parse version as number, default to 1
        const promptVersion =
            typeof versionValue === 'string'
                ? parseInt(versionValue, 10)
                : typeof versionValue === 'number'
                  ? versionValue
                  : 1
        const tempValue =
            config.get('temperature')?.ifString()?.getValue() ??
            config.get('temperature')?.ifNumber()?.getValue() ??
            0.2
        // Parse temperature to number if it's a string
        const temperature = typeof tempValue === 'string' ? parseFloat(tempValue) : tempValue

        // Extract maxTokens handling both string and number types
        const maxTokensValue =
            config.get('max_tokens')?.ifString()?.getValue() ?? config.get('max_tokens')?.ifNumber()?.getValue()
        const maxTokens = typeof maxTokensValue === 'string' ? parseInt(maxTokensValue, 10) : (maxTokensValue ?? 500)

        // Store prompt as raw string here. Mustache-to-arrow-function conversion
        // happens later in convertMustachePromptsToArrowFunctions() after inputNameMap
        // is fully built, so both input and tool references can be resolved.
        const prompt = typeof promptValue === 'string' ? promptValue : ''

        const versionSettings: PromptSettingsVersion = {
            $id: NowIdShape.from(config),
            model: typeof modelValue === 'string' ? modelValue : 'llm_generic_small',
            temperature: temperature,
            ...(provider !== 'Now LLM Generic' ? { maxTokens } : {}),
            prompt,
            // Always include promptState for all versions
            promptState: (stateValue && typeof stateValue === 'string' ? stateValue : 'draft') as
                | 'draft'
                | 'finalized'
                | 'published'
                | 'archived',
            // Include version if not 1 (default)
            ...(promptVersion !== 1 ? { version: promptVersion } : {}),
        }

        // Group by provider
        if (!providerPromptsMap.has(provider)) {
            providerPromptsMap.set(provider, {
                providerAPI,
                promptsByName: new Map(),
            })
        }
        const providerData = providerPromptsMap.get(provider)!

        // Group by prompt name within provider
        if (!providerData.promptsByName.has(promptName)) {
            providerData.promptsByName.set(promptName, { versions: [] })
        }
        const promptData = providerData.promptsByName.get(promptName)!

        // Extract and transform filterCondition for this version from prompt_config
        // filterCondition is now stored per version, not per prompt
        let versionFilterCondition: Record<string, string> | undefined
        if (configId && aiConfigIdToFilterProperties.has(configId)) {
            const filterPropertiesJson = aiConfigIdToFilterProperties.get(configId)!
            try {
                const filterProperties = JSON.parse(filterPropertiesJson)
                // Transform from internal format back to Fluent format
                // Platform stores: {{user_table}} or {{user_table.field.subfield}}
                // Fluent expects: 'User table' (original name) or 'user_table.field.subfield' (normalized + dot-path)
                const transformedFilter: Record<string, string> = {}
                for (const [key, value] of Object.entries(filterProperties)) {
                    // Remove {{ }} wrapper
                    const internalName = key.replace(/^\{\{|\}\}$/g, '')

                    // Check if this is a dot-notation key (e.g., 'user_table.field.subfield')
                    if (internalName.includes('.')) {
                        // For dot-notation keys, keep as-is (already in normalized format)
                        // This matches the Fluent API type: `${ToNormalized<InputName>}.${string}`
                        transformedFilter[internalName] = value as string
                    } else {
                        // For bare input names, try to find the original display name
                        const matchingInput = inputs.find(
                            (inp) => inp.name.toLowerCase().replace(/\s+/g, '_') === internalName
                        )
                        // Use original display name if found, otherwise keep as-is
                        const displayName = matchingInput ? matchingInput.name : internalName
                        transformedFilter[displayName] = value as string
                    }
                }
                // Only add filterCondition if it has entries
                if (Object.keys(transformedFilter).length > 0) {
                    versionFilterCondition = transformedFilter
                }
            } catch (e) {
                // Invalid JSON, skip filterCondition
            }
        }

        // Add filterCondition to the version settings if present
        if (versionFilterCondition) {
            versionSettings.filterCondition = versionFilterCondition
        }
        promptData.versions.push(versionSettings)

        // Track default prompt and version for this provider
        // The ai_config referenced by the prompt config with is_default=true tells us both
        if (configId && aiConfigIdToIsDefault.get(configId)) {
            providerData.defaultPromptName = promptName
            providerData.defaultPromptVersion = promptVersion
        }
    }

    // Determine default provider
    const providerKeys = Array.from(providerPromptsMap.keys())
    const firstProviderName = providerKeys[0] ?? 'Now LLM Generic'
    let defaultProviderName = firstProviderName
    if (defaultDefinitionId && typeof defaultDefinitionId === 'string') {
        defaultProviderName = definitionIdToProvider.get(defaultDefinitionId) ?? firstProviderName
    }

    const providers: ProviderPromptConfig[] = []
    for (const [providerName, providerData] of providerPromptsMap) {
        // Convert Map to array of PromptSettings
        const prompts: PromptSettings[] = []
        for (const [promptName, promptData] of providerData.promptsByName) {
            prompts.push({
                name: promptName,
                versions: promptData.versions,
            })
        }

        // Set fallback default prompt if not specified
        if (!providerData.defaultPromptName && prompts[0]) {
            providerData.defaultPromptName = prompts[0].name
        }

        providers.push({
            provider: providerName,
            // Only include providerAPI if it exists (custom providers or overrides)
            ...(providerData.providerAPI ? { providerAPI: providerData.providerAPI } : {}),
            prompts: prompts,
            // Only include defaultPrompt if multiple prompts exist
            ...(prompts.length > 1 && providerData.defaultPromptName
                ? { defaultPrompt: providerData.defaultPromptName }
                : {}),
            // Only include defaultPromptVersion if defaultPrompt is shown (multiple prompts exist)
            ...(prompts.length > 1 && providerData.defaultPromptVersion
                ? { defaultPromptVersion: providerData.defaultPromptVersion }
                : {}),
        })
    }

    // inputs, outputs, providers, and defaultProviderName are now used directly
    // in the two-argument CallExpressionShape below

    // Build skillSettings with providers array from validators
    const skillSettings: Partial<SkillSettings> = {}
    const providerMap = new Map<string, ProviderConfig>()

    // Process request validators (preprocessors)
    for (const reqValidator of requestValidators) {
        const validatorRef = reqValidator.get('validator')?.getValue()
        const order = reqValidator.get('order')?.ifNumber()?.getValue() ?? 100

        if (validatorRef && typeof validatorRef === 'string') {
            // Find the corresponding validator record
            const validatorRecord = validators.find((v) => v.getId().getValue() === validatorRef)
            if (validatorRecord) {
                const name = validatorRecord.get('name')?.getValue()
                const script = validatorRecord.get('validator_script')?.getValue()
                const validatorSysId = validatorRecord.getId().getValue()

                if (name && typeof name === 'string' && script && typeof script === 'string') {
                    // Extract provider name (remove " - Request Validator" suffix)
                    const providerName = name.replace(/ - Request Validator$/, '')

                    if (!providerMap.has(providerName)) {
                        providerMap.set(providerName, {
                            name: providerName,
                            order,
                            $id: NowIdShape.from(validatorRecord),
                        })
                    }
                    const provider = providerMap.get(providerName)!
                    provider.preprocessor = script
                    if (!provider.order || order < provider.order) {
                        provider.order = order
                    }
                }
            }
        }
    }

    // Process response validators (postprocessors)
    for (const resValidator of responseValidators) {
        const validatorRef = resValidator.get('validator')?.getValue()
        const order = resValidator.get('order')?.ifNumber()?.getValue() || 100

        if (validatorRef && typeof validatorRef === 'string') {
            // Find the corresponding validator record
            const validatorRecord = validators.find((v) => v.getId().getValue() === validatorRef)
            if (validatorRecord) {
                const name = validatorRecord.get('name')?.getValue()
                const script = validatorRecord.get('validator_script')?.getValue()
                const validatorSysId = validatorRecord.getId().getValue()

                if (name && typeof name === 'string' && script && typeof script === 'string') {
                    // Extract provider name (remove " - Response Validator" suffix)
                    const providerName = name.replace(/ - Response Validator$/, '')

                    if (!providerMap.has(providerName)) {
                        providerMap.set(providerName, {
                            name: providerName,
                            order,
                            $id: NowIdShape.from(validatorRecord),
                        })
                    }
                    const provider = providerMap.get(providerName)!
                    provider.postprocessor = script
                    // Use the lower order value between pre and post
                    if (!provider.order || order < provider.order) {
                        provider.order = order
                    }
                }
            }
        }
    }

    if (providerMap.size > 0) {
        skillSettings.providers = Array.from(providerMap.values()).sort((a, b) => (a.order || 100) - (b.order || 100))
    }

    // Build securityControls from ACL and role configuration
    const securityControls: Partial<SecurityControls> = {}

    // Extract ACL data and build userAccess config (MANDATORY field)
    let userAccessGenerated = false

    if (aclRecords.length > 0) {
        const aclRecord = aclRecords[0]
        if (!aclRecord) {
            // ACL record is undefined - this is unexpected, log a error
            diagnostics.error(
                record,
                'ACL record is undefined during XML-to-Fluent transformation. ' +
                    'Defaulting to authenticated user access. This may indicate a data integrity issue.'
            )
        } else {
            const securityAttribute = aclRecord.get('security_attribute')?.getValue()

            // Extract roles from ACL role records — keep as Shape objects (RecordId)
            // so that getCode() serializes using the coalesce key (role name) instead of key-registry GUID
            // This matches the pattern used by the core ACL plugin
            const roles: Shape[] = aclRoleRecords
                .map((aclRoleRecord) => aclRoleRecord.get('sys_user_role'))
                .filter((shape): shape is Shape => shape !== undefined)

            // Determine access type from security_attribute first
            // security_attribute can be:
            // - The string 'user_is_authenticated'
            // - A sys_id reference to the security attribute record
            // - The display value 'UserIsAuthenticated'
            // - Empty/undefined for role-only access
            const attrStr = securityAttribute ? String(securityAttribute).toLowerCase() : ''
            const isAuthenticated = attrStr.includes('authenticated') || attrStr === ACL_CONSTANTS.USER_IS_AUTHENTICATED

            if (isAuthenticated) {
                // Authenticated user access — roles are optional
                securityControls.userAccess = {
                    $id: NowIdShape.from(aclRecord),
                    type: 'authenticated',
                    ...(roles.length > 0 ? { roles } : {}),
                }
                userAccessGenerated = true
            } else if (roles.length > 0) {
                // Role-based access only (no authenticated flag)
                securityControls.userAccess = {
                    $id: NowIdShape.from(aclRecord),
                    type: 'roles',
                    roles: roles,
                }
                userAccessGenerated = true
            }
        }
    }

    // userAccess is mandatory - default to authenticated if not found
    if (!userAccessGenerated) {
        securityControls.userAccess = {
            type: 'authenticated',
        }
    }

    // Extract role restrictions from sys_agent_access_role_configuration
    if (roleConfigRecords.length > 0) {
        const roleConfigRecord = roleConfigRecords[0]
        if (!roleConfigRecord) {
            // Role config record is undefined - this is unexpected, log a error
            diagnostics.error(
                record,
                'Role configuration record is undefined during XML-to-Fluent transformation. ' +
                    'Role restrictions will not be set. This may indicate a data integrity issue.'
            )
        } else {
            // Split read: each storage feeds its own API field — no cross-field dedup.
            //   role_list   → roleRestrictions (sys_ids, legacy column)
            //   mapping rows → roleMap (names extracted from reference attributes)
            // Pre-ZP10 sources: mapping query returns [] (table absent), roleMap stays unset.

            // role_list → roleRestrictions
            const roleList = roleConfigRecord.get('role_list')?.getValue()
            if (roleList) {
                const sysIds =
                    typeof roleList === 'string'
                        ? roleList
                              .split(',')
                              .map((r: string) => r.trim())
                              .filter(Boolean)
                        : Array.isArray(roleList)
                          ? (roleList as string[])
                          : [roleList as string]
                if (sysIds.length > 0) {
                    securityControls.roleRestrictions = sysIds
                }
            }

            // sys_agent_access_role_mapping → roleMap (works for both full and incremental transforms)
            const parentSysId = roleConfigRecord.getId().getValue()
            const mappingRows = descendants.query(SYS_AGENT_ACCESS_ROLE_MAPPING).filter((row) => {
                // Only include mapping rows belonging to this skill's role config
                const fkValue = row.get('agent_access_config')?.toString().getValue()
                return typeof fkValue === 'string' && fkValue === parentSysId
            })
            // Preserve role shapes (RecordId) for coalesce-based serialization and key tracking
            const roleShapes: Shape[] = mappingRows
                .map((row) => row.get('role'))
                .filter((shape): shape is Shape => shape !== undefined)
            if (roleShapes.length > 0) {
                securityControls.roleMap = roleShapes
            }
        }
    }

    // If the source instance has no roles in role_list or sys_agent_access_role_mapping,
    // leave roleRestrictions unset. The user must set it explicitly in TS source.
    // userAccess defaults to authenticated if not set
    if (!securityControls.userAccess) {
        securityControls.userAccess = { type: 'authenticated' }
    }

    // Query tool records and generate tools
    const resourceMappings = descendants.query(SYS_ONE_EXTEND_RESOURCE_MAPPING)
    const attributeMappings = descendants.query(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING)
    const edges = descendants.query(SYS_ONE_EXTEND_RESOURCE_EDGE)

    // For same-scope Skill-as-Tool: the target skill's capability record may not be
    // in descendants (it belongs to the other skill's hierarchy). Look up unresolved
    // resource_capability references from the full project database so generateTools()
    // can check their type (type='skill' → Skill-as-Tool).
    const resolvedCapabilities = [...capabilities]
    const resolvedDefinitionAttributes = [...definitionAttributes]
    if (database) {
        const capIds = new Set(capabilities.map((c) => c.getId().getValue() as string))
        const defAttrIds = new Set(definitionAttributes.map((a) => a.getId().getValue() as string))
        for (const rm of resourceMappings) {
            const rcId = rm.get('resource_capability')?.getValue() as string
            if (rcId && !capIds.has(rcId) && rcId !== capabilityId) {
                // Try to find this capability in the full project database
                const cap = database.get(SYS_ONE_EXTEND_CAPABILITY, rcId)
                if (cap) {
                    resolvedCapabilities.push(cap)
                    capIds.add(rcId)
                    // Also resolve the target skill's definition attributes
                    // so that Skill-as-Tool input names can be resolved
                    const targetDefAttrs = database.query(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE, {
                        capability: rcId,
                    })
                    for (const attr of targetDefAttrs) {
                        const attrId = attr.getId().getValue() as string
                        if (!defAttrIds.has(attrId)) {
                            resolvedDefinitionAttributes.push(attr)
                            defAttrIds.add(attrId)
                        }
                    }
                }
            }
        }
    }

    const { tools, unsupportedTools, unsupportedMetrics, supportedToolsToDelete } = generateTools(
        resourceMappings,
        attributeMappings,
        edges,
        resolvedCapabilities,
        capabilityDefinitions,
        capabilityId as string,
        resolvedDefinitionAttributes
    )

    // Build input name mapping: normalizedName → originalName
    // XML stores lowercased input names (e.g., 'userquery'), but the original Fluent code
    // uses camelCase (e.g., 'userQuery'). This map restores the original casing.
    // All inputs must be added to enable mustache → Fluent conversion (e.g., {{input1}} → ${p.input.input1})
    const inputNameMap = new Map<string, string>()
    for (const inp of inputs) {
        const normalized = inp.name.toLowerCase().replace(/ +/g, '_')
        inputNameMap.set(normalized, inp.name)
    }

    // Build toolNames set for validation and prompt conversion
    const toolNames = new Set(tools?.map((t) => t.name) ?? [])

    // Validate: skill input names must not conflict with tool names
    if (tools && tools.length > 0) {
        for (const inp of inputs) {
            if (toolNames.has(inp.name)) {
                diagnostics.error(
                    skillConfig,
                    `Skill input name '${inp.name}' conflicts with tool name '${inp.name}'. ` +
                        'Input names and tool names must be unique to avoid ambiguous template references.'
                )
            }
        }
    }

    // Convert prompts containing mustache template references to arrow function syntax.
    // Mustache patterns like {{ToolName.outputField}} and {{inputfield}} are converted to
    // arrow function: (p) => `...${p.tool.ToolName.outputField}...${p.input.fieldName}...`
    // This must happen after inputNameMap is built so we can restore original casing.
    // Only converts references that match valid inputs or tools - unknown references are left as-is.
    convertMustachePromptsToArrowFunctions(providers, inputNameMap, toolNames, skillConfig)

    // Extract existing tool variable names from the current code's tools arrow function.
    // When existing code has `const inline = t.InlineScript('AllToolsInline', ...)`,
    // we want to preserve "inline" as the variable name instead of generating "AllToolsInline".
    // For fresh transforms (no existing code), this map will be empty and sanitizeVarName is used.
    const existingToolVarNames = new Map<string, string>()
    try {
        // skillConfig.getSource() returns CallExpressionShape (the shape from the build phase).
        // The shape's own getSource() returns the original ts.Node (the AST CallExpression).
        const sourceShape = skillConfig.getSource?.()
        const sourceNode = (sourceShape as unknown as { getSource?: () => unknown })?.getSource?.()
        const node =
            sourceNode && typeof sourceNode === 'object' && 'getKind' in sourceNode
                ? (sourceNode as ts.Node)
                : undefined
        if (node && ts.Node.isCallExpression(node)) {
            const configObj = node.getArguments()[0]
            if (configObj && ts.Node.isObjectLiteralExpression(configObj)) {
                const toolsProp = configObj.getProperty('tools')
                if (toolsProp && ts.Node.isPropertyAssignment(toolsProp)) {
                    const arrowFn = toolsProp.getInitializer()
                    if (arrowFn && ts.Node.isArrowFunction(arrowFn)) {
                        for (const stmt of arrowFn.getStatements()) {
                            if (!ts.Node.isVariableStatement(stmt)) {
                                continue
                            }
                            const decl = stmt.getDeclarations()[0]
                            if (!decl) {
                                continue
                            }
                            const varName = decl.getName()
                            const init = decl.getInitializer()
                            if (init && ts.Node.isCallExpression(init)) {
                                const toolNameArg = init.getArguments()[0]
                                if (toolNameArg && ts.Node.isStringLiteral(toolNameArg)) {
                                    existingToolVarNames.set(toolNameArg.getLiteralValue(), varName)
                                }
                            }
                        }
                    }
                }
            }
        }
    } catch {
        // Source not available (e.g. fresh transform from XML) — use sanitizeVarName defaults
    }

    // Build tools arrow function shape if tools exist
    const toolsArrowFunction =
        tools?.length > 0
            ? buildToolsArrowFunctionShape(tools, skillConfig, inputNameMap, existingToolVarNames)
            : undefined

    // Pre-compute all values to avoid SDK reference tracking issues during transform
    const stateValue = skillConfig.get('state')?.getValue()
    const isPublished = stateValue === '1'

    // Build the shape - always include all fields to avoid conditional spread issues
    // The SDK has issues with conditional spreads in toShape transforms
    const shape = new CallExpressionShape({
        source: skillConfig,
        callee: 'NowAssistSkillConfig',
        args: [
            // Arg 1: Skill definition (metadata, inputs, outputs, tools, security)
            skillConfig.transform(({ $ }) => {
                const skillNameVal = skillConfig.get('name')?.getValue()
                const shortDescVal = capability.get('short_description')?.getValue() || ''
                const descVal =
                    skillConfig.get('description')?.getValue() || capability.get('description')?.getValue() || ''

                // Build base object with required fields
                const baseObj: Record<string, ReturnType<typeof $.val>> = {
                    $id: $.val(NowIdShape.from(capability)),
                    name: $.val(skillNameVal),
                    shortDescription: $.val(shortDescVal),
                    description: $.val(descVal),
                    inputs: $.val(inputs),
                    outputs: $.val(outputs),
                }
                if (isPublished) {
                    baseObj['state'] = $.val('published' as const)
                }
                if (toolsArrowFunction) {
                    baseObj['tools'] = $.val(toolsArrowFunction)
                }
                if (skillSettings.providers && skillSettings.providers.length > 0) {
                    // Build providers array with explicit $id handling to ensure NowIdShape is serialized correctly
                    const providersArray = skillSettings.providers.map((provider) => {
                        const providerObj: Record<string, unknown> = {}
                        // Add $id first if present (NowIdShape needs to be passed directly for proper serialization)
                        if (provider.$id) {
                            providerObj['$id'] = provider.$id
                        }
                        providerObj['name'] = provider.name
                        if (provider.order !== undefined) {
                            providerObj['order'] = provider.order
                        }
                        if (provider.preprocessor) {
                            providerObj['preprocessor'] = provider.preprocessor
                        }
                        if (provider.postprocessor) {
                            providerObj['postprocessor'] = provider.postprocessor
                        }
                        return providerObj
                    })
                    baseObj['skillSettings'] = $.val({ providers: providersArray })
                }
                if (securityControls.userAccess) {
                    // Build securityControls with explicit $id handling to ensure NowIdShape is serialized correctly
                    const userAccessObj: Record<string, unknown> = {
                        type: securityControls.userAccess.type,
                    }
                    // Add $id if present (NowIdShape needs to be passed directly to $.val for proper serialization)
                    if (securityControls.userAccess.$id) {
                        userAccessObj['$id'] = securityControls.userAccess.$id
                    }
                    // Add roles if present
                    if (securityControls.userAccess.roles && securityControls.userAccess.roles.length > 0) {
                        userAccessObj['roles'] = securityControls.userAccess.roles
                    }
                    const scObj: Record<string, unknown> = {
                        userAccess: userAccessObj,
                    }
                    if (securityControls.roleRestrictions && securityControls.roleRestrictions.length > 0) {
                        scObj['roleRestrictions'] = securityControls.roleRestrictions
                    }
                    if (securityControls.roleMap && securityControls.roleMap.length > 0) {
                        scObj['roleMap'] = securityControls.roleMap
                    }
                    baseObj['securityControls'] = $.val(scObj)
                }
                if (deployConfigData) {
                    baseObj['deploymentSettings'] = $.val(deployConfigData)
                }

                return baseObj
            }),
            // Arg 2: Prompt configuration (providers, defaultProvider)
            skillConfig.transform(({ $ }) => {
                const baseObj: Record<string, ReturnType<typeof $.val>> = {
                    providers: $.val(providers),
                }
                if (providers.length > 1) {
                    baseObj['defaultProvider'] = $.val(defaultProviderName)
                }
                return baseObj
            }),
        ],
    })

    // Pre-calculate attribute mappings for excluded metrics (used in multiple return blocks)
    // excludedRecords contains both auto-tuned configs AND excluded metric resource_mappings
    // We need to return the attribute_mappings for those excluded metrics as Record API
    const excludedMetricIds = new Set(
        excludedRecords
            .filter((r) => r.getTable() === SYS_ONE_EXTEND_RESOURCE_MAPPING)
            .map((r) => r.getId().getValue() as string)
    )
    const excludedMetricAttrMappings = attributeMappings.filter((a) =>
        excludedMetricIds.has(a.get('resource_mapping')?.getValue() as string)
    )

    // If unsupported TOOLS exist → return ALL tool records to Record API (dependencies broken)
    if (unsupportedTools.length > 0) {
        // Include tool-level definition attributes in unhandled records.
        // Without this, tool def attrs are neither in the Fluent shape (which only includes
        // skill-level attrs where capability === capabilityId) nor in Record API output.
        // The build then marks them as deleted, generating action="DELETE" XML that removes
        // the def attrs from the instance — breaking attribute mapping references.
        const toolDefinitionAttributes = definitionAttributes.filter(
            (attr) => attr.get('capability')?.getValue() !== capabilityId
        )
        const unhandled = [
            ...resourceMappings,
            ...attributeMappings,
            ...edges,
            ...toolDefinitionAttributes,
            ...supportedToolsToDelete,
        ]
        return {
            success: 'partial' as const,
            value: shape,
            unhandledRecords: [...unhandled, ...excludedRecords],
        }
    }

    // If only unsupported METRICS → return ONLY those metric mappings to Record API
    if (unsupportedMetrics.length > 0) {
        const metricIds = new Set(unsupportedMetrics.map((m) => m.getId().getValue() as string))
        const metricAttrMappings = attributeMappings.filter((a) =>
            metricIds.has(a.get('resource_mapping')?.getValue() as string)
        )
        return {
            success: 'partial' as const,
            value: shape,
            unhandledRecords: [
                ...unsupportedMetrics,
                ...metricAttrMappings,
                ...supportedToolsToDelete,
                ...excludedRecords,
                ...excludedMetricAttrMappings,
            ],
        }
    }

    // If there are Record API tool files to delete, return partial with DELETE records
    if (supportedToolsToDelete.length > 0) {
        return {
            success: 'partial' as const,
            value: shape,
            unhandledRecords: [...supportedToolsToDelete, ...excludedRecords, ...excludedMetricAttrMappings],
        }
    }

    // If only excludedRecords exist (no tool/metric issues)
    if (excludedRecords.length > 0) {
        return {
            success: 'partial' as const,
            value: shape,
            unhandledRecords: [...excludedRecords, ...excludedMetricAttrMappings],
        }
    }

    return {
        success: true,
        value: shape,
    }
}
